/**
 * RolePage.jsx — one layout for every FusionMSI role page.
 * Shaped by prompt 38; MKT-01, MKT-20, MKT-23, MKT-25. D-38-02, D-38-05.
 * Invariants: one filled button per screen; a role page never grants access;
 * Holds copy is not rendered. Closing actions are outlined when the hero
 * already has the filled button.
 * Related: rolePageCopyData.js, rolePageAccess.js, RolePageRoute.jsx.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import FusionMsiMarketingLayout from '../components/FusionMsiMarketingLayout';
import { ROLE_PAGE_PREVIEW_BANNER } from '../marketing/rolePageCopyData';
import {
  ROLE_FOOTER_LINK_LABELS,
  resolveRoleCta,
  roleFooterHref,
  rolePageNeedsNoindex,
} from '../marketing/rolePageAccess';

function useRolePageMeta(page, view) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTitle = document.title;
    if (page?.headline) document.title = page.headline;

    const noindex = rolePageNeedsNoindex(page, view);
    let tag = null;
    if (noindex) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      tag.setAttribute('content', 'noindex');
      tag.setAttribute('data-role-page', '1');
      document.head.appendChild(tag);
    }
    return () => {
      document.title = previousTitle;
      if (tag) tag.remove();
    };
  }, [page, view]);
}

function RoleCta({ cta, slug, filled, testId }) {
  const dest = resolveRoleCta(cta, slug);
  if (!cta) return null;
  if (!dest) {
    return (
      <Typography variant="body1" data-testid={testId}>
        {cta.label}
      </Typography>
    );
  }
  const buttonProps = {
    variant: filled ? 'contained' : 'outlined',
    'data-testid': testId,
    sx: { minHeight: 44, alignSelf: 'flex-start' },
  };
  if (dest.kind === 'scroll') {
    return (
      <Button
        {...buttonProps}
        onClick={() => {
          const node = document.getElementById(dest.target);
          if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {cta.label}
      </Button>
    );
  }
  return (
    <Button {...buttonProps} component={RouterLink} to={dest.href}>
      {cta.label}
    </Button>
  );
}

RoleCta.propTypes = {
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    action: PropTypes.string.isRequired,
    topic: PropTypes.string,
    target: PropTypes.string,
    slug: PropTypes.string,
  }),
  slug: PropTypes.string,
  filled: PropTypes.bool,
  testId: PropTypes.string,
};

function SectionItems({ section }) {
  const items = (section.items || []).filter((item) => item.body || item.title || item.note);
  if (section.numbered) {
    return (
      <Box component="ol" sx={{ mt: 1, pl: 3 }}>
        {items.map((item) => (
            <Box component="li" key={`${item.title || ''}-${item.body || item.note || ''}`} id={item.id} sx={{ mb: 1.5 }}>
            <ItemText item={item} />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {items.map((item) => (
        <Box key={`${item.title || ''}-${item.body || item.note || ''}`} id={item.id}>
          <ItemText item={item} />
        </Box>
      ))}
    </Stack>
  );
}

SectionItems.propTypes = {
  section: PropTypes.shape({
    numbered: PropTypes.bool,
    items: PropTypes.array,
  }).isRequired,
};

function ItemText({ item }) {
  if (item.note) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {item.note}
      </Typography>
    );
  }
  if (item.title && item.body) {
    return (
      <Typography variant="body1">
        <Box component="span" sx={{ fontWeight: 600 }}>{item.title} </Box>
        {item.body}
      </Typography>
    );
  }
  return <Typography variant="body1">{item.title || item.body}</Typography>;
}

ItemText.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    note: PropTypes.string,
  }).isRequired,
};

function PageSections({ page }) {
  const sections = [...(page.sections || [])];
  if (page.decisionPoint && !page.decisionPoint.pending) {
    sections.push(page.decisionPoint);
  }
  return sections.map((section) => (
    <Box
      key={section.title}
      id={section.id}
      component="section"
      sx={{ mt: 4 }}
      data-testid={section.title === 'DecisionPoint' ? 'fusionmsi.rolePage.decisionPoint' : 'fusionmsi.rolePage.section'}
    >
      <Typography variant="h5" component="h2">
        {section.title}
      </Typography>
      {section.intro ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {section.intro}
        </Typography>
      ) : null}
      <SectionItems section={section} />
    </Box>
  ));
}

PageSections.propTypes = {
  page: PropTypes.shape({
    sections: PropTypes.array,
    decisionPoint: PropTypes.object,
  }).isRequired,
};

export function RolePageBody({ page, view }) {
  useRolePageMeta(page, view);
  const heroFilled = Boolean(page.primary && page.primary.action !== 'none');
  const showPreview = view === 'preview';

  // Paper surface: the marketing backdrop is navy, and text.primary is dark.

  return (
    <FusionMsiMarketingLayout maxWidth="md">
      <Box
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 1,
          p: { xs: 3, sm: 4 },
          border: 1,
          borderColor: 'divider',
        }}
      >
      {showPreview ? (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="fusionmsi.rolePage.previewBanner">
          {ROLE_PAGE_PREVIEW_BANNER}
        </Alert>
      ) : null}
      {page.eyebrow ? (
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {page.eyebrow}
        </Typography>
      ) : null}
      <Typography variant="h3" component="h1" data-testid="fusionmsi.rolePage.headline">
        {page.headline}
      </Typography>
      {page.subhead ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {page.subhead}
        </Typography>
      ) : null}
      {page.primary ? (
        <Box sx={{ mt: 3 }}>
          <RoleCta
            cta={page.primary}
            slug={page.slug}
            filled={heroFilled}
            testId="fusionmsi.rolePage.primary"
          />
        </Box>
      ) : null}
      <PageSections page={page} />
      {page.closing ? (
        <Box sx={{ mt: 4 }}>
          {page.closing.headline ? (
            <Typography variant="h5" component="h2">
              {page.closing.headline}
            </Typography>
          ) : null}
          {page.closing.line ? (
            <Typography variant="body1" sx={{ mt: 1 }}>
              {page.closing.line}
            </Typography>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            {page.closing.primary ? (
              <RoleCta
                cta={page.closing.primary}
                slug={page.slug}
                filled={!heroFilled}
                testId={heroFilled ? 'fusionmsi.rolePage.closing' : 'fusionmsi.rolePage.primary'}
              />
            ) : null}
            {page.closing.secondary ? (
              <RoleCta
                cta={page.closing.secondary}
                slug={page.slug}
                filled={false}
                testId="fusionmsi.rolePage.secondary"
              />
            ) : null}
          </Stack>
          {page.closing.secondaryLine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {page.closing.secondaryLine}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {page.footer ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }} data-testid="fusionmsi.rolePage.footer">
          {page.footer}
        </Typography>
      ) : null}
      {page.footerLinks ? (
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          {page.footerLinks.map((key) => {
            const href = roleFooterHref(key);
            if (!href) return null;
            return (
              <Button key={key} component={RouterLink} to={href} sx={{ minHeight: 44 }}>
                {ROLE_FOOTER_LINK_LABELS[key]}
              </Button>
            );
          })}
        </Stack>
      ) : null}
      </Box>
    </FusionMsiMarketingLayout>
  );
}

RolePageBody.propTypes = {
  page: PropTypes.object.isRequired,
  view: PropTypes.oneOf(['preview', 'content']).isRequired,
};

export function RoleStaffSignIn({ page }) {
  const signedOut = page.signedOut || {
    headline: 'MD3C staff sign-in',
    primary: { label: 'Sign in', action: 'signIn' },
  };
  useRolePageMeta({ ...page, headline: signedOut.headline }, 'staff-sign-in');
  return (
    <FusionMsiMarketingLayout maxWidth="sm">
      <Box
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 1,
          p: { xs: 3, sm: 4 },
          border: 1,
          borderColor: 'divider',
        }}
      >
      <Typography variant="h3" component="h1" data-testid="fusionmsi.rolePage.headline">
        {signedOut.headline}
      </Typography>
      <Box sx={{ mt: 3 }}>
        <RoleCta
          cta={signedOut.primary}
          slug={page.slug}
          filled
          testId="fusionmsi.rolePage.primary"
        />
      </Box>
      </Box>
    </FusionMsiMarketingLayout>
  );
}

RoleStaffSignIn.propTypes = {
  page: PropTypes.object.isRequired,
};
